import React, { useState, useEffect, useMemo } from 'react';
import { Building2, Calendar, Package, Award, AlertTriangle, CheckCircle2, Clock } from 'lucide-react';
import { readApiError } from '../common/apiError';

interface Institute {
  _id: string;
  name: string;
  status?: string;
  subscriptionPlan?: string;
  subscriptionStatus?: string;
  subscriptionEndDate?: string;
  licensedSeats?: number;
}

interface DeploymentEntry {
  _id: string;
  deploymentNumber?: number;
  title: string;
  status: string;
  scheduledDate?: string;
  modules?: Array<{ name: string }>;
  institutes?: Array<{ _id: string; name: string }>;
  deployment?: { deployedAt?: string };
}

const STATUS_COLORS: Record<string, string> = {
  pending: 'bg-gray-100 text-gray-700',
  tl_approved: 'bg-sky-100 text-sky-700',
  pm_approved: 'bg-indigo-100 text-indigo-700',
  deployed: 'bg-emerald-100 text-emerald-700',
  rejected: 'bg-rose-100 text-rose-700',
  rolled_back: 'bg-amber-100 text-amber-700',
};

const InstituteStatusView: React.FC = () => {
  const [institutes, setInstitutes] = useState<Institute[]>([]);
  const [deployments, setDeployments] = useState<DeploymentEntry[]>([]);
  const [selectedId, setSelectedId] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    const getArray = async (url: string, label: string) => {
      const r = await fetch(url);
      if (!r.ok) throw new Error(await readApiError(r, `Failed to load ${label}`));
      const data = await r.json();
      return Array.isArray(data) ? data : [];
    };
    Promise.all([
      getArray('/api/institutes', 'institutes'),
      getArray('/api/deployments/all', 'deployments')
    ]).then(([inst, deps]) => {
      setInstitutes(inst);
      setDeployments(deps);
      if (inst.length > 0) setSelectedId(inst[0]._id || inst[0].id);
      setLoading(false);
    }).catch(err => {
      console.error('Error loading institute status:', err);
      setLoadError(err instanceof Error ? err.message : 'Failed to load institute status');
      setLoading(false);
    });
  }, []);

  const selectedInstitute = institutes.find(i => i._id === selectedId || (i as any).id === selectedId);

  const relevantDeployments = useMemo(() => {
    if (!selectedId) return [];
    return deployments
      .filter(d => d.institutes?.some(i => i._id === selectedId))
      .sort((a, b) => new Date(b.scheduledDate || 0).getTime() - new Date(a.scheduledDate || 0).getTime());
  }, [deployments, selectedId]);

  const upcoming = relevantDeployments.filter(d => !['deployed', 'rejected', 'rolled_back'].includes(d.status));
  const deployed = relevantDeployments.filter(d => d.status === 'deployed');

  const expiryInfo = useMemo(() => {
    if (!selectedInstitute?.subscriptionEndDate) return null;
    const daysLeft = Math.ceil((new Date(selectedInstitute.subscriptionEndDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
    return daysLeft;
  }, [selectedInstitute]);

  if (loading) {
    return <div className="text-center py-12 text-gray-500">Loading...</div>;
  }

  if (loadError) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <p className="text-red-600">{loadError}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Building2 className="w-6 h-6 text-gray-700" />
          Institute Status
        </h1>
        <p className="text-gray-600 mt-1">Deployment activity and subscription status for a client institute</p>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">Select Institute</label>
        <select
          value={selectedId}
          onChange={(e) => setSelectedId(e.target.value)}
          className="w-full md:w-96 px-3 py-2 border border-gray-300 rounded-lg"
        >
          {institutes.map(i => (
            <option key={i._id} value={i._id}>{i.name}</option>
          ))}
        </select>
      </div>

      {selectedInstitute && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
              <p className="text-sm text-gray-600 mb-1">Plan</p>
              <p className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <Award className="w-5 h-5 text-violet-600" />
                {selectedInstitute.subscriptionPlan || 'basic'}
              </p>
            </div>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
              <p className="text-sm text-gray-600 mb-1">Subscription Status</p>
              <p className="text-xl font-bold flex items-center gap-2">
                {selectedInstitute.subscriptionStatus === 'active' ? (
                  <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                ) : (
                  <AlertTriangle className="w-5 h-5 text-rose-600" />
                )}
                <span className={selectedInstitute.subscriptionStatus === 'active' ? 'text-emerald-700' : 'text-rose-700'}>
                  {selectedInstitute.subscriptionStatus || 'active'}
                </span>
              </p>
            </div>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
              <p className="text-sm text-gray-600 mb-1">Licensed Seats</p>
              <p className="text-xl font-bold text-gray-900">{selectedInstitute.licensedSeats || 0}</p>
            </div>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
              <p className="text-sm text-gray-600 mb-1">Renewal</p>
              <p className={`text-xl font-bold ${expiryInfo !== null && expiryInfo <= 30 ? 'text-amber-600' : 'text-gray-900'}`}>
                {expiryInfo === null ? '—' : expiryInfo < 0 ? 'Expired' : `${expiryInfo}d left`}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Clock className="w-5 h-5 text-amber-600" />
                Upcoming / In Progress ({upcoming.length})
              </h3>
              {upcoming.length === 0 ? (
                <p className="text-sm text-gray-500">Nothing scheduled right now.</p>
              ) : (
                <div className="space-y-2">
                  {upcoming.map(d => (
                    <div key={d._id} className="p-3 bg-gray-50 rounded-lg flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{d.title}</p>
                        {d.scheduledDate && (
                          <p className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                            <Calendar className="w-3 h-3" />
                            {new Date(d.scheduledDate).toLocaleString()}
                          </p>
                        )}
                      </div>
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${STATUS_COLORS[d.status] || 'bg-gray-100 text-gray-700'}`}>
                        {d.status.replace('_', ' ')}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Package className="w-5 h-5 text-emerald-600" />
                Recently Deployed ({deployed.length})
              </h3>
              {deployed.length === 0 ? (
                <p className="text-sm text-gray-500">Nothing deployed to this institute yet.</p>
              ) : (
                <div className="space-y-2">
                  {deployed.slice(0, 10).map(d => (
                    <div key={d._id} className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-sm font-medium text-gray-900">{d.title}</p>
                      {d.deployment?.deployedAt && (
                        <p className="text-xs text-gray-500 mt-0.5">
                          Deployed {new Date(d.deployment.deployedAt).toLocaleDateString()}
                        </p>
                      )}
                      {d.modules && d.modules.length > 0 && (
                        <p className="text-xs text-gray-500 mt-0.5">Modules: {d.modules.map(m => m.name).join(', ')}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default InstituteStatusView;
